import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";

import { Card, CardHeader } from "../Card/Card";
import { Button } from "../Button";
import { Modal } from "../Modal";
import { Typography } from "../Typography";
import { UnderlinedButtonStyle } from "./shared";
import { CONTACT_TYPES_TEXT, TEST_ID_PREFIX } from "./constants";
import { ContactCardInfo } from "./ContactCardInfo";
import { ContactListEdit } from "./ContactListEdit";
import { UpdateWarningModal } from "./UpdateWarningModal";
import type { ContactCardProps } from "./types";

const ContactCard = ({
  contactType,
  contactList,
  selectedContactId: selectedTempContactId,
  disabled,
  disableSameSignerValidation,
  customApprovedStatusText,
  customNotApprovedStatusText,
  customNotCheckedStatusText,
  hasOffer,
  inProgress,
  initialEditState = false,
  onCancelEdit,
  onSelectionUpdate = () => Promise.resolve(),
  onContactInfoSave,
  onRunCreditCheck,
  onRefreshCreditCheck,
  disableEditEmail,
  toolTipText,
}: ContactCardProps) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<null | string>(
    null
  );
  const [isEdit, setIsEdit] = useState(initialEditState);

  useEffect(() => {
    setSelectedContactId(null);
  }, [contactType, contactList]);

  useEffect(() => {
    setIsEdit(initialEditState); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactType]);

  useEffect(() => {
    // Should only execute once when the component is mounted to setup Secondary Contact properly
    if (contactType === "secondary" && selectedTempContactId !== undefined) {
      setSelectedContactId(selectedTempContactId);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedContact = contactList?.find((contact) => {
    if (selectedContactId) {
      return contact.contactId === selectedContactId;
    }

    switch (contactType) {
      case "utility_bill":
        return contact.derivedRoles?.includes("UtilityBillContact");
      case "secondary":
        return contact.derivedRoles?.includes("Contract Cosigner");
      case "primary":
      default:
        return !!contact.primary;
    }
  });

  const handleRemove = async (showConfirmModal: boolean) => {
    if (showConfirmModal) {
      return setShowConfirmationModal(true);
    }

    setShowConfirmationModal(false);
    try {
      await onSelectionUpdate(undefined);
      setIsEdit(false);
      setSelectedContactId(null);
    } catch (ex) {
      // Stay expanded because save attempt was rejected
    }
  };

  const showAddSecondaryButton =
    contactType === "secondary" && !selectedContact && !isEdit;

  if (showAddSecondaryButton) {
    return (
      <Button
        color="secondary"
        onClick={() => setIsEdit(true)}
        data-testid="secondary-signer-select-add"
      >
        Add Secondary Signer
      </Button>
    );
  }

  return (
    <CardStyled dataTestId={`${TEST_ID_PREFIX[contactType]}-card-container`}>
      <CardHeader
        contactType={contactType}
        toolTipText={toolTipText}
        title={CONTACT_TYPES_TEXT[contactType]}
        rightContent={
          <>
            {contactType === "secondary" && (
              <UnderlinedButtonStyle
                size="xs"
                color="tertiary"
                onClick={() => handleRemove(true)}
                style={{ visibility: !isEdit ? "visible" : "hidden" }}
                data-testid={`${TEST_ID_PREFIX[contactType]}-contact-remove`}
              >
                Remove
              </UnderlinedButtonStyle>
            )}
            <UnderlinedButtonStyle
              size="xs"
              color="tertiary"
              onClick={() => setIsEdit(true)}
              style={{ visibility: !isEdit ? "visible" : "hidden" }}
              data-testid={`${TEST_ID_PREFIX[contactType]}-contact-edit`}
            >
              Edit
            </UnderlinedButtonStyle>
          </>
        }
      />
      {!isEdit && (
        <ContactCardInfo
          contact={selectedContact}
          contactType={contactType}
          customApprovedStatusText={customApprovedStatusText}
          customNotApprovedStatusText={customNotApprovedStatusText}
          customNotCheckedStatusText={customNotCheckedStatusText}
          onRunCreditCheck={onRunCreditCheck}
          onRefreshCreditCheck={onRefreshCreditCheck}
          inProgress={inProgress}
        />
      )}

      {isEdit && (
        <ContactListEdit
          contactType={contactType}
          contactList={contactList}
          selectedContactId={selectedContact?.contactId}
          selectedTempContactId={selectedTempContactId}
          disableSameSignerValidation={disableSameSignerValidation}
          disabled={disabled}
          hasOffer={hasOffer}
          onCancel={() => {
            setIsEdit(false);
            onCancelEdit?.();
          }}
          onSelectionUpdate={async (selectedContact) => {
            try {
              await onSelectionUpdate(selectedContact);
              setSelectedContactId(selectedContact.contactId);
              setIsEdit(false);
            } catch {
              // Stay expanded because save attempt was rejected
            }
          }}
          onContactInfoSave={onContactInfoSave}
          customApprovedStatusText={customApprovedStatusText}
          customNotApprovedStatusText={customNotApprovedStatusText}
          customNotCheckedStatusText={customNotCheckedStatusText}
          disableEditEmail={disableEditEmail}
        />
      )}

      {showConfirmationModal && contactType === "secondary" && hasOffer && (
        <UpdateWarningModal
          contactType={contactType}
          onClose={() => {
            setShowConfirmationModal(false);
          }}
          onConfirm={() => handleRemove(false)}
        />
      )}

      {showConfirmationModal && contactType === "secondary" && !hasOffer && (
        <Modal
          type="warning"
          title="Are you sure you want to remove the secondary signer?"
          primaryButton={{
            text: "Yes, remove",
            testId: `${TEST_ID_PREFIX[contactType]}-modal-confirm-remove`,
            onClick: () => {
              handleRemove(false);
            },
          }}
          onClose={() => {
            setShowConfirmationModal(false);
          }}
          secondaryButton={{
            text: "Cancel",
            testId: `${TEST_ID_PREFIX[contactType]}-modal-cancel-remove`,
            onClick: () => {
              setShowConfirmationModal(false);
            },
          }}
        >
          <Typography role="secondary">
            If you remove the secondary signer "
            {selectedContact?.customerFirstName}{" "}
            {selectedContact?.customerLastName}" they will no longer be a
            secondary signer on the Sunrun agreement. Do you want to proceed?
          </Typography>
        </Modal>
      )}
    </CardStyled>
  );
};

const CardStyled = styled(Card)`
  .modal-wrapper {
    padding: 0;
    margin: 0;
  }
`;

export { ContactCard };
